// src/shared/utils/platform.js
// Platform-specific utilities for Capacitor

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

/**
 * Check if running on native platform
 * @returns {boolean}
 */
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on Android
 * @returns {boolean}
 */
export const isAndroid = () => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on iOS
 * @returns {boolean}
 */
export const isIOS = () => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running on web
 * @returns {boolean}
 */
export const isWeb = () => {
  return Capacitor.getPlatform() === 'web';
};

/**
 * Get current platform name
 * @returns {string} 'android', 'ios', or 'web'
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

// ==================== STATUS BAR ====================

/**
 * Set status bar to light style (dark text)
 */
export const setStatusBarLight = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.setStyle({ style: Style.Light });
  } catch (error) {
    console.warn('StatusBar not available:', error);
  }
};

/**
 * Set status bar to dark style (light text)
 */
export const setStatusBarDark = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.setStyle({ style: Style.Dark });
  } catch (error) {
    console.warn('StatusBar not available:', error);
  }
};

/**
 * Set status bar background color
 * @param {string} color - Hex color code
 */
export const setStatusBarColor = async (color) => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.setBackgroundColor({ color });
  } catch (error) {
    console.warn('StatusBar not available:', error);
  }
};

/**
 * Hide status bar
 */
export const hideStatusBar = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.hide();
  } catch (error) {
    console.warn('StatusBar not available:', error);
  }
};

/**
 * Show status bar
 */
export const showStatusBar = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.show();
  } catch (error) {
    console.warn('StatusBar not available:', error);
  }
};

// ==================== KEYBOARD ====================

/**
 * Show keyboard
 */
export const showKeyboard = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await Keyboard.show();
  } catch (error) {
    console.warn('Keyboard not available:', error);
  }
};

/**
 * Hide keyboard
 */
export const hideKeyboard = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await Keyboard.hide();
  } catch (error) {
    console.warn('Keyboard not available:', error);
  }
};

/**
 * Add keyboard show listener
 * @param {Function} callback
 * @returns {Promise<PluginListenerHandle>}
 */
export const addKeyboardShowListener = async (callback) => {
  if (!isNativePlatform()) return null;
  
  try {
    return await Keyboard.addListener('keyboardWillShow', callback);
  } catch (error) {
    console.warn('Keyboard listeners not available:', error);
    return null;
  }
};

/**
 * Add keyboard hide listener
 * @param {Function} callback
 * @returns {Promise<PluginListenerHandle>}
 */
export const addKeyboardHideListener = async (callback) => {
  if (!isNativePlatform()) return null;
  
  try {
    return await Keyboard.addListener('keyboardWillHide', callback);
  } catch (error) {
    console.warn('Keyboard listeners not available:', error);
    return null;
  }
};

// ==================== HAPTICS ====================

/**
 * Trigger light haptic feedback
 */
export const hapticLight = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (error) {
    console.warn('Haptics not available:', error);
  }
};

/**
 * Trigger medium haptic feedback
 */
export const hapticMedium = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (error) {
    console.warn('Haptics not available:', error);
  }
};

/**
 * Trigger heavy haptic feedback
 */
export const hapticHeavy = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await Haptics.impact({ style: ImpactStyle.Heavy });
  } catch (error) {
    console.warn('Haptics not available:', error);
  }
};

/**
 * Trigger vibration pattern
 */
export const vibrate = async (duration = 100) => {
  if (!isNativePlatform()) return;
  
  try {
    await Haptics.vibrate({ duration });
  } catch (error) {
    console.warn('Haptics not available:', error);
  }
};

// ==================== APP ====================

/**
 * Add back button listener (Android)
 * @param {Function} callback - Function to call when back button is pressed
 * @returns {Promise<PluginListenerHandle>}
 */
export const addBackButtonListener = async (callback) => {
  if (!isNativePlatform()) return null;
  
  try {
    return await App.addListener('backButton', callback);
  } catch (error) {
    console.warn('Back button listener not available:', error);
    return null;
  }
};

/**
 * Add app state change listener
 * @param {Function} callback - Function to call when app state changes
 * @returns {Promise<PluginListenerHandle>}
 */
export const addAppStateListener = async (callback) => {
  if (!isNativePlatform()) return null;
  
  try {
    return await App.addListener('appStateChange', callback);
  } catch (error) {
    console.warn('App state listener not available:', error);
    return null;
  }
};

/**
 * Exit app (Android only)
 */
export const exitApp = async () => {
  if (!isAndroid()) return;
  
  try {
    await App.exitApp();
  } catch (error) {
    console.warn('Exit app not available:', error);
  }
};

// ==================== SAFE AREAS ====================

/**
 * Get safe area insets
 * @returns {Object} {top, bottom, left, right}
 */
export const getSafeAreaInsets = () => {
  if (!isNativePlatform()) {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }
  
  // Get CSS variables set by Capacitor
  const style = getComputedStyle(document.documentElement);
  
  return {
    top: parseInt(style.getPropertyValue('--safe-area-inset-top') || '0'),
    bottom: parseInt(style.getPropertyValue('--safe-area-inset-bottom') || '0'),
    left: parseInt(style.getPropertyValue('--safe-area-inset-left') || '0'),
    right: parseInt(style.getPropertyValue('--safe-area-inset-right') || '0')
  };
};

/**
 * Apply safe area padding to element
 * @param {HTMLElement} element
 */
export const applySafeAreaPadding = (element) => {
  if (!element || !isNativePlatform()) return;
  
  element.style.paddingTop = 'env(safe-area-inset-top)';
  element.style.paddingBottom = 'env(safe-area-inset-bottom)';
  element.style.paddingLeft = 'env(safe-area-inset-left)';
  element.style.paddingRight = 'env(safe-area-inset-right)';
};

// ==================== DEEP LINKING ====================

/**
 * Add URL open listener (for deep links)
 * @param {Function} callback
 * @returns {Promise<PluginListenerHandle>}
 */
export const addUrlOpenListener = async (callback) => {
  if (!isNativePlatform()) return null;
  
  try {
    return await App.addListener('appUrlOpen', callback);
  } catch (error) {
    console.warn('URL open listener not available:', error);
    return null;
  }
};

// ==================== UTILITY ====================

/**
 * Check if feature is available
 * @param {string} pluginName
 * @returns {boolean}
 */
export const isPluginAvailable = (pluginName) => {
  return Capacitor.isPluginAvailable(pluginName);
};

/**
 * Get app info
 * @returns {Promise<Object>}
 */
export const getAppInfo = async () => {
  if (!isNativePlatform()) {
    return {
      name: 'Manifest',
      version: '0.1.0',
      build: '1',
      platform: 'web'
    };
  }
  
  try {
    const info = await App.getInfo();
    return {
      ...info,
      platform: getPlatform()
    };
  } catch (error) {
    console.warn('App info not available:', error);
    return null;
  }
};

/**
 * Log platform information (useful for debugging)
 */
export const logPlatformInfo = async () => {
  console.log('📱 Platform Info:', {
    platform: getPlatform(),
    isNative: isNativePlatform(),
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    isWeb: isWeb()
  });
  
  const appInfo = await getAppInfo();
  console.log('📦 App Info:', appInfo);
  
  const safeArea = getSafeAreaInsets();
  console.log('🔒 Safe Area Insets:', safeArea);
};
